import type {
  ShopifyTemplateAssignedQueryVariables,
  IShopifyPageAssignedResponse,
} from '~/modules/shopify';
import type { ITemplateAssignedType } from '~/types/template';

import { useQueryShopifyAssignedPaged, useQueryShopifyTemplateAssigned } from '~/modules/shopify';
import { TEMPLATE_ASSIGNED } from '~/constants/template';
import { useAssignedStore } from '~/stores/assigned';
import { TemplateType } from '~/types/template';
import { useEffect, useMemo } from 'react';

export const useFetchTemplateAssigned = ({
  activePageType,
  pagination = false,
}: {
  activePageType: ITemplateAssignedType;
  isSingleFetcher?: boolean;
  pagination?: boolean;
}) => {
  const assignedLoading = useAssignedStore((state) => state.loading);
  const assigned = useAssignedStore((state) => state.assigned);
  const clearCache = useAssignedStore((state) => state.clearCache);
  const clearCacheTypes = useAssignedStore((state) => state.clearCacheTypes);
  const setClearCache = useAssignedStore((state) => state.setClearCache);
  const removeClearCacheType = useAssignedStore((state) => state.removeClearCacheType);
  const setAssignedByType = useAssignedStore((state) => state.setAssignedByType);
  const setUnassignedByType = useAssignedStore((state) => state.setUnassignedByType);
  const setLoadingByType = useAssignedStore((state) => state.setLoadingByType);

  const isPageTypeValid = useMemo(() => {
    return !!activePageType && TEMPLATE_ASSIGNED.includes(activePageType);
  }, [activePageType]);

  // Set by whoever knows this type's data just changed (e.g. a template published seconds ago).
  const isClearCacheByType = useMemo(
    () => clearCacheTypes.includes(activePageType),
    [clearCacheTypes, activePageType],
  );

  const variables = useMemo<ShopifyTemplateAssignedQueryVariables>(() => {
    return JSON.parse(
      JSON.stringify({
        type: activePageType,
        clearCache: clearCache || isClearCacheByType,
      }),
    );
  }, [activePageType, clearCache, isClearCacheByType]);

  const templateAssigned = useMemo(() => {
    return assigned?.[activePageType];
  }, [assigned, activePageType]);

  const isFetched = useMemo(() => {
    return assigned && activePageType in assigned;
  }, [assigned, activePageType]);

  const isEnableQueryAssigned = useMemo(() => {
    if (activePageType === TemplateType.HOME) return false;
    if (isFetched && variables.clearCache) return true;

    return !!activePageType && isPageTypeValid && !isFetched && !templateAssigned;
  }, [activePageType, isPageTypeValid, isFetched, templateAssigned, variables]);

  const handleSetAssignedByType = (data: IShopifyPageAssignedResponse) => {
    setUnassignedByType(activePageType);
    setAssignedByType(activePageType, data);
    setLoadingByType(activePageType, false);
    removeClearCacheType(activePageType);
  };

  // Release the one-shot flag and the loading gate on failure too. Left set, the flag keeps this
  // type's query permanently enabled and re-bypasses the server cache on every mount, and the
  // gate holds the listing on its skeleton for the rest of the session.
  const handleFetchAssignedFailed = () => {
    setLoadingByType(activePageType, false);
    removeClearCacheType(activePageType);
  };

  // --- pagination=false: use existing query (single request, cached) ---
  const { isLoading, isRefetching, fetchManual } = useQueryShopifyTemplateAssigned(variables, {
    enabled: isEnableQueryAssigned && !pagination,
    staleTime: 500,
    onSuccess: handleSetAssignedByType,
    onError: handleFetchAssignedFailed,
  });

  // --- pagination=true: use paged query, stream data into store progressively ---
  const {
    data: pagedData,
    isLoading: isPagedLoading,
    isDone,
    refetch: refetchPaged,
  } = useQueryShopifyAssignedPaged({ type: activePageType }, isEnableQueryAssigned && pagination);

  useEffect(() => {
    if (!pagination || isPagedLoading) return;
    if (Object.keys(pagedData).length === 0) return;

    setAssignedByType(activePageType, pagedData);

    if (isDone) {
      setUnassignedByType(activePageType);
      setLoadingByType(activePageType, false);
      removeClearCacheType(activePageType);
    }
  }, [pagedData, isDone, pagination, isPagedLoading, activePageType]); // eslint-disable-line

  const isLoadingAssigned = useMemo(() => {
    if (pagination) return isPagedLoading;
    return isLoading || isRefetching;
  }, [pagination, isPagedLoading, isLoading, isRefetching]);

  const handleRefetchAssigned = async (templateType: TemplateType) => {
    if (templateType === TemplateType.HOME) return;

    setAssignedByType(activePageType, {});

    if (pagination) {
      refetchPaged();
      return;
    }

    const { data } = await fetchManual({ type: templateType, clearCache: true }, true);
    handleSetAssignedByType(data);
  };

  useEffect(() => {
    if (!clearCache) return;
    if (Object.values(assignedLoading).every((value) => !value)) {
      setClearCache(false);
    }
  }, [assignedLoading, clearCache, setClearCache]);

  return {
    templateAssigned,
    isLoadingAssigned,
    isEnableQueryAssigned,
    handleRefetchAssigned,
  };
};
